import GoogleAutocomplete from './GoogleAutocomplete/GoogleAutocomplete'
import { useState } from 'react'
import { Input } from '@web/components/atoms/Input'
import { AddressInput } from '@web/graphql-types'
import { Field } from '@web/components/atoms/Field'

type AddressFieldProps = {
  onChange: (fullAddress: string, address: AddressInput) => void
  value: string
  address: AddressInput | undefined | null
  apiKey: string
  isRequired?: boolean
  isAlwaysManualEntry?: boolean
}

const toFormattedAddress = (address: AddressInput) => {
  // example: 639 Lonsdale St, Melbourne VIC 3000, Australia
  const { addressLine1, city, state, postcode, country } = address
  return [addressLine1, `${[city, state, postcode].filter((x) => x).join(' ')}`, country].filter((x) => x).join(', ')
}

const AddressField = ({
  value,
  address,
  onChange,
  apiKey,
  isRequired,
  isAlwaysManualEntry = false,
}: AddressFieldProps) => {
  const [isManualEntry, setIsManualEntry] = useState(isAlwaysManualEntry)

  const handleManualOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAddress = { ...address, [e.currentTarget.name]: e.currentTarget.value }
    onChange(toFormattedAddress(newAddress), newAddress)
  }

  const isAllEmpty =
    !isRequired &&
    !address?.addressLine1 &&
    !address?.city &&
    !address?.state &&
    !address?.postcode &&
    !address?.country

  return (
    <>
      <GoogleAutocomplete
        apiKey={apiKey}
        address={address}
        onChange={onChange}
        initialValue={value}
        onSwitchToManualEntry={() => setIsManualEntry(true)}
        isManual={isManualEntry}
        isRequired={isRequired}
      />
      {isManualEntry && (
        <>
          <Field label="Address Line 1" required={isRequired} invalid={!isAllEmpty && !address?.addressLine1}>
            <Input
              id="address-line-1"
              name="addressLine1"
              value={address?.addressLine1 ?? ''}
              required={isRequired}
              onChange={handleManualOnChange}
            />
          </Field>
          <Field id="city" label="Suburb" required={isRequired} invalid={!isAllEmpty && !address?.city}>
            <Input
              id="city"
              name="city"
              value={address?.city ?? ''}
              required={isRequired}
              onChange={handleManualOnChange}
            />
          </Field>
          <Field id="state" label="State" required={isRequired} invalid={!isAllEmpty && !address?.state}>
            <Input
              id="state"
              name="state"
              value={address?.state ?? ''}
              required={isRequired}
              onChange={handleManualOnChange}
            />
          </Field>
          <Field id="post-code" label="Postcode" required={isRequired} invalid={!isAllEmpty && !address?.postcode}>
            <Input
              id="post-code"
              name="postcode"
              value={address?.postcode ?? ''}
              required={isRequired}
              onChange={handleManualOnChange}
            />
          </Field>
          <Field id="country" label="Country" required={isRequired} invalid={!isAllEmpty && !address?.country}>
            <Input
              id="country"
              name="country"
              value={address?.country ?? ''}
              required={isRequired}
              onChange={handleManualOnChange}
            />
          </Field>
        </>
      )}
    </>
  )
}

export default AddressField
